import { useZippopotamQuery } from "@/common/api/third-party-services/zippopotam";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { PropertyMap } from "@/modules/Properties/components/PropertyMap/PropertyMap";
import { propertyFormSchema } from "@/modules/Properties/schema/propertyFormSchema";
import { useQueryClient } from "@tanstack/react-query";
import { Building2, Check, Flag, Globe2, Loader2, Mail, MapPin, MapPinned, Move, Search } from "lucide-react";
import { useEffect, useState } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface PropertyLocationProps {
    form: UseFormReturn<z.infer<typeof propertyFormSchema>>;
    isEditing: boolean;
}

export const PropertyLocation = ({ form, isEditing }: PropertyLocationProps) => {
    const { t } = useTranslation();
    const { toast } = useToast();
    const queryClient = useQueryClient();
    const zippopotamQuery = useZippopotamQuery();
    const [isSearching, setIsSearching] = useState(false);
    const [locationInfo, setLocationInfo] = useState<any>(null);
    const [isGeoLocationUpdating, setIsGeoLocationUpdating] = useState(false);

    // Відстежуємо всі поля, пов'язані з локацією - але нічого не робимо автоматично при їх зміні
    const formValues = useWatch({
        control: form.control
    });

    const { geoLocation, address, city, state, country, name, postalCode } = formValues;

    // Функція для пошуку геолокації за адресою - викликається тільки за клік по кнопці
    const searchGeoLocationByAddress = async () => {
        if (!address || !city || !state || !country) return;

        try {
            setIsGeoLocationUpdating(true);

            // Формуємо повну адресу для пошуку
            const fullAddress = `${address}, ${city}, ${state}, ${country}`;

            // Використовуємо Nominatim API (OpenStreetMap) для пошуку координат
            const response = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(fullAddress)}`);
            const data = await response.json();

            if (data && data.length > 0) {
                const { lat, lon } = data[0];

                // Оновлюємо геолокацію у формі
                form.setValue('geoLocation', {
                    type: 'Point',
                    coordinates: [parseFloat(lon), parseFloat(lat)]
                });

                toast({
                    title: t("properties.location.applySuccess.title"),
                    description: t("properties.location.applySuccess.description"),
                });
            }
        } catch (error) {
            console.error("Failed to fetch geolocation by address:", error);
        } finally {
            setIsGeoLocationUpdating(false);
        }
    };

    // Пошук локації за поштовим індексом - тільки за явного кліку
    const handleSearchLocation = () => {
        if (!postalCode) {
            toast({
                variant: "destructive",
                title: t("properties.location.searchError.title"),
                description: t("properties.location.searchError.missingFields"),
            });
            return;
        }

        setIsSearching(true);
        setLocationInfo(null);

        queryClient.fetchQuery(
            zippopotamQuery.locationByPostalCodeQuery("US", postalCode)
        ).then((data) => {
            setLocationInfo(data);

            if (data && data.places && data.places.length > 0) {
                const place = data.places[0];

                form.setValue("city", place["place name"]);
                form.setValue("state", place.state);
                form.setValue("stateAbbreviation", place["state abbreviation"]);
                form.setValue("country", data.country);
                form.setValue("countryAbbreviation", data["country abbreviation"]);

                // Створюємо GeoJSON Point об'єкт з координатами
                if (place.longitude && place.latitude) {
                    const geoLocation = {
                        type: "Point",
                        coordinates: [parseFloat(place.longitude), parseFloat(place.latitude)]
                    };
                    form.setValue("geoLocation", geoLocation);
                }

                toast({
                    title: t("properties.location.applySuccess.title"),
                    description: t("properties.location.applySuccess.description"),
                });
            }
        }).catch((error) => {
            console.error("Failed to fetch location data:", error);
            toast({
                variant: "destructive",
                title: t("properties.location.searchError.title"),
                description: t("properties.location.searchError.notFound"),
            });
        }).finally(() => {
            setIsSearching(false);
        });
    };

    // Кнопка для оновлення геолокації за адресою
    const handleUpdateGeoLocation = () => {
        searchGeoLocationByAddress();
    };

    // Обробник зміни координат при перетягуванні маркера
    const handleLocationChange = (lat: number, lng: number) => {
        // Оновлюємо геолокацію у формі
        form.setValue('geoLocation', {
            type: 'Point',
            coordinates: [lng, lat]
        });

        toast({
            title: t("properties.location.markerMoved"),
            description: t("properties.location.coordinatesUpdated"),
        });
    };

    return (
        <div className="space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle className="text-lg font-medium flex items-center gap-2">
                        <MapPinned className="h-5 w-5 text-primary" />
                        {t("properties.sections.location")}
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {/* Приховане поле для geoLocation */}
                    <FormField
                        control={form.control}
                        name="geoLocation"
                        render={({ field }) => (
                            <FormItem className="hidden">
                                <FormControl>
                                    <Input
                                        type="hidden"
                                        {...field}
                                        value={field.value ? JSON.stringify(field.value) : ""}
                                        onChange={(e) => {
                                            try {
                                                const value = e.target.value ? JSON.parse(e.target.value) : null;
                                                field.onChange(value);
                                            } catch (error) {
                                                field.onChange(null);
                                            }
                                        }}
                                    />
                                </FormControl>
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="address"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel className="flex items-center gap-2">
                                    <MapPin className="h-4 w-4 text-muted-foreground" />
                                    {t("properties.form.address")}
                                </FormLabel>
                                <FormControl>
                                    <Input
                                        {...field}
                                        disabled={!isEditing}
                                        placeholder={t("properties.form.addressPlaceholder") || "Enter street address"}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <div className="grid grid-cols-2 gap-4">
                        <FormField
                            control={form.control}
                            name="city"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="flex items-center gap-2">
                                        <Building2 className="h-4 w-4 text-muted-foreground" />
                                        {t("properties.form.city")}
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            disabled={!isEditing}
                                            placeholder={t("properties.form.cityPlaceholder") || "Enter city"}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="postalCode"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="flex items-center gap-2">
                                        <Mail className="h-4 w-4 text-muted-foreground" />
                                        {t("properties.form.postalCode")}
                                    </FormLabel>
                                    <div className="flex gap-2">
                                        <FormControl>
                                            <Input
                                                {...field}
                                                disabled={!isEditing}
                                                placeholder={t("properties.form.postalCodePlaceholder") || "Enter postal code"}
                                            />
                                        </FormControl>
                                        {isEditing && (
                                            <Button
                                                type="button"
                                                variant="outline"
                                                size="icon"
                                                onClick={handleSearchLocation}
                                                disabled={isSearching || !postalCode}
                                                className="flex-shrink-0"
                                                title={t("properties.location.search")}
                                            >
                                                {isSearching ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    <Search className="h-4 w-4" />
                                                )}
                                            </Button>
                                        )}
                                    </div>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <FormField
                            control={form.control}
                            name="state"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="flex items-center gap-2">
                                        <MapPinned className="h-4 w-4 text-muted-foreground" />
                                        {t("properties.form.state")}
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            disabled={!isEditing}
                                            placeholder={t("properties.form.statePlaceholder") || "Enter state"}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="stateAbbreviation"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="flex items-center gap-2">
                                        <MapPin className="h-4 w-4 text-muted-foreground" />
                                        {t("properties.form.stateAbbreviation")}
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            disabled={!isEditing}
                                            placeholder={t("properties.form.stateAbbreviationPlaceholder") || "Enter state code"}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                        <FormField
                            control={form.control}
                            name="country"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="flex items-center gap-2">
                                        <Globe2 className="h-4 w-4 text-muted-foreground" />
                                        {t("properties.form.country")}
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            disabled={!isEditing}
                                            placeholder={t("properties.form.countryPlaceholder") || "Enter country"}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <FormField
                            control={form.control}
                            name="countryAbbreviation"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel className="flex items-center gap-2">
                                        <Flag className="h-4 w-4 text-muted-foreground" />
                                        {t("properties.form.countryAbbreviation")}
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            {...field}
                                            disabled={!isEditing}
                                            placeholder={t("properties.form.countryAbbreviationPlaceholder") || "Enter country code"}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    </div>

                    {/* Поля для координат (відображаються тільки в режимі редагування) */}
                    {isEditing && (
                        <div className="border-t pt-4 mt-4">
                            <div className="flex justify-between items-center mb-3">

                                <div className="flex gap-2">
                                    {address && city && state && country && (
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={handleUpdateGeoLocation}
                                            disabled={isGeoLocationUpdating}
                                        >
                                            {isGeoLocationUpdating ? (
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            ) : (
                                                <MapPin className="h-4 w-4 mr-2" />
                                            )}
                                            {t('properties.location.updateFromAddress')}
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <FormItem>
                                    <FormLabel>{t('properties.location.latitude')}</FormLabel>
                                    <FormControl>
                                        <Input
                                            type="number"
                                            step="0.000001"
                                            placeholder="Latitude (e.g. 30.3264)"
                                            value={geoLocation?.coordinates?.[1] || ''}
                                            onChange={(e) => {
                                                const lat = parseFloat(e.target.value);
                                                const lng = geoLocation?.coordinates?.[0] || 0;
                                                if (!isNaN(lat)) {
                                                    form.setValue('geoLocation', {
                                                        type: 'Point',
                                                        coordinates: [lng, lat]
                                                    });
                                                }
                                            }}
                                        />
                                    </FormControl>
                                </FormItem>
                                <FormItem>
                                    <FormLabel>{t('properties.location.longitude')}</FormLabel>
                                    <FormControl>
                                        <Input
                                            type="number"
                                            step="0.000001"
                                            placeholder="Longitude (e.g. -97.7713)"
                                            value={geoLocation?.coordinates?.[0] || ''}
                                            onChange={(e) => {
                                                const lng = parseFloat(e.target.value);
                                                const lat = geoLocation?.coordinates?.[1] || 0;
                                                if (!isNaN(lng)) {
                                                    form.setValue('geoLocation', {
                                                        type: 'Point',
                                                        coordinates: [lng, lat]
                                                    });
                                                }
                                            }}
                                        />
                                    </FormControl>
                                </FormItem>
                            </div>
                        </div>
                    )}
                </CardContent>
            </Card>
            {geoLocation && (
                <PropertyMap
                    geoLocation={geoLocation}
                    address={address}
                    city={city}
                    state={state}
                    country={country}
                    name={name}
                    isEditing={isEditing}
                    onLocationChange={isEditing ? handleLocationChange : undefined}
                />
            )}
        </div>
    );
}; 